import React, { useEffect, useState } from "react";
import { ExternalLink } from "lucide-react";

import { Button } from "../../../components/ui/button";
import { Textarea } from "../../../components/ui/textarea";
import { useNotification } from "../../../contexts/NotificationContext";
import { usePricing } from "../../../contexts/PricingContext";
import { configSchema, flattenIssues } from "../../../lib/pricing";
import { saveConfig } from "../../pricing/repository";
import { Field, FormSection, NumberField, SwitchField, TextField, inputClass } from "./fields";

export default function ConfigTab() {
  const { data } = usePricing();
  const { showNotification } = useNotification();
  const [form, setForm] = useState(() => toForm(data.config));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!dirty) setForm(toForm(data.config));
  }, [data.config, dirty]);

  const patch = (changes: Partial<ReturnType<typeof toForm>>) => {
    setDirty(true);
    setForm((prev) => ({ ...prev, ...changes }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const parsed = configSchema.safeParse({
      ...form,
      maxPassengers: form.maxPassengers === "" ? NaN : form.maxPassengers,
      roundTrip: {
        enabled: form.roundTripEnabled,
        multiplier: form.roundTripMultiplier === "" ? NaN : form.roundTripMultiplier,
      },
    });
    if (!parsed.success) {
      setErrors(flattenIssues(parsed.error));
      return;
    }
    setErrors({});
    setSaving(true);
    try {
      await saveConfig(parsed.data);
      setDirty(false);
      showNotification("success", "Configurações salvas.");
    } catch {
      showNotification("error", "Não foi possível salvar as configurações.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="space-y-6" onSubmit={handleSubmit}>
      <FormSection title="Site">
        <SwitchField
          label="Exibir orçamento no site"
          description="Desligue para esconder a página /orcamento e o link no menu."
          checked={form.enabled}
          onChange={(enabled) => patch({ enabled })}
        />
        <Button
          type="button"
          variant="ghost"
          className="w-full justify-start text-white/80 hover:bg-white/10 hover:text-white"
          onClick={() => window.open("/orcamento", "_blank")}
        >
          <ExternalLink className="h-4 w-4" /> Abrir página de orçamento
        </Button>
      </FormSection>

      <FormSection title="WhatsApp">
        <TextField
          id="config-whatsapp"
          label="Número que recebe os pedidos *"
          inputMode="tel"
          placeholder="5548999999999"
          value={form.whatsappNumber}
          onChange={(whatsappNumber) => patch({ whatsappNumber })}
          error={errors.whatsappNumber}
          hint="Somente dígitos, com DDI e DDD. Ex.: 5548996275321"
        />
      </FormSection>

      <FormSection title="Regras do cálculo">
        <NumberField
          id="config-max-pax"
          label="Máximo de passageiros *"
          min={1}
          max={50}
          value={form.maxPassengers}
          onChange={(maxPassengers) => patch({ maxPassengers })}
          error={errors.maxPassengers}
          hint="Acima disso o cliente vê 'sob consulta'."
        />
        <SwitchField
          label="Permitir ida e volta"
          description="Mostra a opção de ida e volta no formulário."
          checked={form.roundTripEnabled}
          onChange={(roundTripEnabled) => patch({ roundTripEnabled })}
        />
        {form.roundTripEnabled && (
          <NumberField
            id="config-multiplier"
            label="Multiplicador de ida e volta *"
            min={1}
            max={3}
            step="0.1"
            value={form.roundTripMultiplier}
            onChange={(roundTripMultiplier) => patch({ roundTripMultiplier })}
            error={errors["roundTrip.multiplier"]}
            hint="2 = dobra o valor do trecho. 1,8 = ida e volta com 10% de desconto."
          />
        )}
      </FormSection>

      <FormSection title="Aviso ao cliente">
        <Field label="Texto exibido junto da estimativa" htmlFor="config-disclaimer" error={errors.disclaimer}>
          <Textarea
            id="config-disclaimer"
            rows={3}
            maxLength={300}
            value={form.disclaimer}
            onChange={(e) => patch({ disclaimer: e.target.value })}
            className={inputClass}
          />
        </Field>
      </FormSection>

      <Button type="submit" className="w-full" disabled={saving || !dirty}>
        Salvar configurações
      </Button>
    </form>
  );
}

function toForm(config: ReturnType<typeof usePricing>["data"]["config"]) {
  return {
    enabled: config.enabled,
    whatsappNumber: config.whatsappNumber,
    currency: config.currency,
    maxPassengers: config.maxPassengers as number | "",
    roundTripEnabled: config.roundTrip.enabled,
    roundTripMultiplier: config.roundTrip.multiplier as number | "",
    disclaimer: config.disclaimer,
  };
}
